import { ForwardedRef, forwardRef, ReactNode } from 'react';
import React from 'react';
import { ArrowBack } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, BoxProps, IconButton, Typography } from '@mui/material';

interface WrapperProps extends BoxProps {
  /**
   * Function that will be execute when click in close icon
   */
  onClose: () => void;
  /**
   * Function that will be execute when click in arrow back icon
   */
  onBack: () => void;
  /**
   * Flag which indicates that has a qrcode
   */
  isShowingQRcode?: boolean;
  /**
   * children, content and button, always in that order
   */
  children: ReactNode[];
}

export const Wrapper = (
  { children, onClose, onBack, isShowingQRcode, ...rest }: WrapperProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  return (
    <Box
      ref={ref}
      sx={theme => ({
        backgroundColor: 'background.paper',
        backfaceVisibility: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        left: '50%',
        maxHeight: '100%',
        paddingBottom: 4,
        paddingX: 2,
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        [theme.breakpoints.up('md')]: {
          borderRadius: '10px',
          height: 'fit-content',
          paddingX: 0,
          width: '456px',
        },
      })}
      {...rest}
    >
      <Box
        component="header"
        sx={theme => ({
          alignItems: 'center',
          color: 'text.primary',
          display: 'flex',
          paddingY: 2,
          position: 'relative',
          textAlign: 'center',
          width: '100%',
          [theme.breakpoints.up('md')]: {
            paddingX: 2,
            position: 'fixed',
            zIndex: 1,
          },
        })}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={onBack}
          sx={theme => ({
            [theme.breakpoints.up('md')]: {
              display: isShowingQRcode ? 'flex' : 'none',
            },
          })}
        >
          <ArrowBack />
        </IconButton>

        <Typography
          component="h3"
          data-testid="title"
          sx={theme => ({
            color: 'inherit',
            flex: 1,
            paddingRight: '20px',
            [theme.breakpoints.up('md')]: {
              display: 'none',
            },
          })}
          variant="h6"
        >
          {isShowingQRcode ? 'QRCode' : 'Compartilhar página'}
        </Typography>

        <IconButton
          color="inherit"
          edge="end"
          size="small"
          onClick={onClose}
          sx={theme => ({
            backgroundColor: 'background.paper',
            boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.4)',
            display: 'none',
            marginLeft: 'auto',
            '&:hover': {
              backgroundColor: 'background.paper',
            },
            [theme.breakpoints.up('md')]: {
              display: 'flex',
            },
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={theme => ({
          backgroundColor: 'background.paper',
          borderRadius: '10px',
          boxShadow: '0px 0px 4px rgb(0 0 0 / 10%)',
          height: 'fit-content',
          marginBottom: 3,
          marginX: 'auto',
          overflowY: 'auto',
          paddingBottom: 3,
          width: '100%',
          [theme.breakpoints.up('md')]: {
            borderRadius: 0,
            boxShadow: 'none',
          },
        })}
      >
        {children[0]}
      </Box>

      {children[1]}
    </Box>
  );
};

export default forwardRef(Wrapper);
